import { doc, getDoc, setDoc, collection, query, where, orderBy, limit as firestoreLimit, getDocs, runTransaction } from 'firebase/firestore';
import { db } from '../../../core/config/firebase';
import { getAnswerWords } from './wordListLoader';

/**
 * Get current date in Eastern Time formatted as YYYY-MM-DD
 * @returns {string} - Date string in YYYY-MM-DD format
 */
export function getEasternDate() {
  const dateString = new Date().toLocaleDateString('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  });

  // Convert MM/DD/YYYY to YYYY-MM-DD
  const [month, day, year] = dateString.split('/');
  return `${year}-${month}-${day}`;
}

/**
 * Get countdown to next day in Eastern Time
 * @returns {Object} - {hours, minutes, seconds}
 */
export function getTimeUntilNextDay() {
  const now = new Date();
  const easternNow = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));

  const tomorrow = new Date(easternNow);
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(0, 0, 0, 0);

  const diff = tomorrow - easternNow;

  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  const seconds = Math.floor((diff % (1000 * 60)) / 1000);

  return { hours, minutes, seconds };
}

/**
 * Get recently used words from the last 90 days
 * @returns {Promise<string[]>} - Array of recently used words
 */
async function getRecentlyUsedWords() {
  try {
    const dailyWordsRef = collection(db, 'dailyWords');

    // Get date 90 days ago
    const ninetyDaysAgo = new Date();
    ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);
    const ninetyDaysAgoString = ninetyDaysAgo.toISOString().split('T')[0];

    const q = query(
      dailyWordsRef,
      where('dateString', '>=', ninetyDaysAgoString),
      orderBy('dateString', 'desc')
    );

    const querySnapshot = await getDocs(q);
    const usedWords = [];

    querySnapshot.forEach((doc) => {
      const data = doc.data();
      if (data.word) {
        usedWords.push(data.word.toUpperCase());
      }
    });

    return usedWords;
  } catch (error) {
    console.error('Error fetching recently used words:', error);
    return [];
  }
}

/**
 * Select a random word that hasn't been used in the last 90 days
 * @returns {Promise<string>} - Random word
 */
async function selectRandomWord() {
  try {
    const answerWords = getAnswerWords(); // Get answer words from loaded list
    const recentWords = await getRecentlyUsedWords();

    // Filter out recently used words
    const availableWords = answerWords.filter(word =>
      !recentWords.includes(word.toUpperCase())
    );

    // If all words have been used (unlikely), reset and use full list
    const wordPool = availableWords.length > 0 ? availableWords : answerWords;

    // Select random word
    const randomIndex = Math.floor(Math.random() * wordPool.length);
    return wordPool[randomIndex].toUpperCase();
  } catch (error) {
    console.error('Error selecting random word:', error);
    // Fallback to truly random word
    const answerWords = getAnswerWords();
    if (answerWords.length > 0) {
      const randomIndex = Math.floor(Math.random() * answerWords.length);
      return answerWords[randomIndex].toUpperCase();
    }
    throw new Error('No answer words available');
  }
}

/**
 * Get or create today's daily word
 * FIXED: Uses atomic transaction to prevent race conditions
 * Multiple simultaneous users will all get the SAME word
 * @returns {Promise<Object>} - {word: string, dateString: string, date: Date}
 */
export async function getTodaysDailyWord() {
  try {
    const todayString = getEasternDate();
    const dailyWordRef = doc(db, 'dailyWords', todayString);

    // First, do a quick non-transactional check (optimization)
    // This avoids the transaction overhead for 99% of requests
    const quickCheck = await getDoc(dailyWordRef);
    if (quickCheck.exists()) {
      const data = quickCheck.data();
      console.log(`✅ Retrieved existing daily word for ${todayString}`);
      return {
        word: data.word,
        dateString: todayString,
        date: data.date.toDate()
      };
    }

    // Word doesn't exist - select it BEFORE transaction
    // This ensures same word is used even if transaction retries
    const newWord = await selectRandomWord();
    const now = new Date();

    // Use transaction to atomically create if still doesn't exist
    const result = await runTransaction(db, async (transaction) => {
      const dailyWordDoc = await transaction.get(dailyWordRef);

      if (dailyWordDoc.exists()) {
        // Another user created it while we were selecting a word
        // Return their word to ensure consistency
        const data = dailyWordDoc.data();
        return {
          word: data.word,
          dateString: todayString,
          date: data.date.toDate(),
          wasCreated: false
        };
      }

      // Still doesn't exist - create it with our pre-selected word
      transaction.set(dailyWordRef, {
        word: newWord,
        dateString: todayString,
        date: now,
        createdAt: now,
        totalAttempts: 0
      });

      return {
        word: newWord,
        dateString: todayString,
        date: now,
        wasCreated: true
      };
    });

    if (result.wasCreated) {
      console.log(`✅ Created new daily word for ${todayString}: ${result.word}`);
    } else {
      console.log(`✅ Another user created word for ${todayString}, using theirs`);
    }

    return {
      word: result.word,
      dateString: result.dateString,
      date: result.date
    };
  } catch (error) {
    console.error('❌ Error getting/creating daily word:', error);
    throw error;
  }
}

/**
 * Check if user has already played today's daily challenge
 * @param {string} userId - The user's Firebase Auth ID
 * @returns {Promise<Object|null>} - User's score document if played, null otherwise
 */
export async function checkUserPlayedToday(userId) {
  try {
    const todayString = getEasternDate();

    const scoresRef = collection(db, 'scores');
    const q = query(
      scoresRef,
      where('userId', '==', userId),
      where('isDaily', '==', true),
      where('dailyWordDate', '==', todayString),
      firestoreLimit(1)
    );

    const querySnapshot = await getDocs(q);

    if (querySnapshot.empty) {
      return null;
    }

    const scoreDoc = querySnapshot.docs[0];
    return {
      id: scoreDoc.id,
      ...scoreDoc.data()
    };
  } catch (error) {
    console.error('Error checking if user played today:', error);
    throw error;
  }
}

/**
 * Increment the total attempts counter for today's word
 * FIXED: Uses Firestore increment() for atomic updates (no race conditions)
 * @param {string} dateString - The date string (YYYY-MM-DD)
 */
export async function incrementDailyWordAttempts(dateString) {
  try {
    const { increment: firestoreIncrement, updateDoc } = await import('firebase/firestore');
    const dailyWordRef = doc(db, 'dailyWords', dateString);

    // Use Firestore's atomic increment operation
    // This is much better than read-modify-write pattern
    await updateDoc(dailyWordRef, {
      totalAttempts: firestoreIncrement(1)
    });
  } catch (error) {
    console.error('Error incrementing daily word attempts:', error);
    // Don't throw - this is not critical
  }
}
